/**
 * FormSection Component
 * Collapsible form sections with title
 * Used for grouping form fields in long forms
 */

import React, { useState } from 'react';
import { Card, Typography, Space } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { spacing } from '../../theme/designTokens';

const { Title } = Typography;

/**
 * FormSection Component
 *
 * @param {string} title - Section title
 * @param {ReactNode} children - Form fields
 * @param {boolean} collapsible - Allow collapse/expand (default: false)
 * @param {boolean} defaultCollapsed - Initially collapsed (default: false)
 * @param {ReactNode} extra - Extra content in header (right-aligned)
 * @param {boolean} bordered - Show border (default: true)
 * @param {Object} style - Additional styles
 */
function FormSection({
  title,
  children,
  collapsible = false,
  defaultCollapsed = false,
  extra,
  bordered = true,
  style = {},
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const handleToggle = () => {
    if (collapsible) {
      setCollapsed(!collapsed);
    }
  };

  return (
    <Card
      bordered={bordered}
      style={{ marginBottom: spacing.lg, ...style }}
      title={
        <div
          onClick={handleToggle}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: collapsible ? 'pointer' : 'default',
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            {title}
          </Title>
          <Space>
            {extra}
            {collapsible && (
              <span style={{ padding: '4px' }}>
                {collapsed ? <DownOutlined /> : <UpOutlined />}
              </span>
            )}
          </Space>
        </div>
      }
    >
      {!collapsed && children}
    </Card>
  );
}

export default FormSection;
